import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const AUTOPLAY_MS = 6000;

export default function HeroSlider({ settings }) {
    // Build slides: prefer hero_slides, fall back to the legacy single hero_image
    const slides = useMemo(() => {
        const list = (settings?.hero_slides || []).filter((s) => s && s.image);
        if (list.length > 0) return list;
        if (settings?.hero_image) {
            return [{
                image: settings.hero_image,
                headline: settings.hero_headline || 'Elegant, Effortless Style.',
                subtext: settings.hero_subtext || '',
                cta_label: settings.hero_cta_label || 'SHOP THE LOOK',
                cta_link: settings.hero_cta_link || '/shop',
            }];
        }
        return [];
    }, [settings]);

    const [current, setCurrent] = useState(0);
    const [paused, setPaused] = useState(false);
    const count = slides.length;
    const timer = useRef(null);

    const goTo = useCallback((i) => {
        if (count === 0) return;
        setCurrent(((i % count) + count) % count);
    }, [count]);
    const next = useCallback(() => goTo(current + 1), [current, goTo]);
    const prev = useCallback(() => goTo(current - 1), [current, goTo]);

    // Autoplay
    useEffect(() => {
        if (count <= 1 || paused) return undefined;
        timer.current = setInterval(() => {
            setCurrent((c) => (c + 1) % count);
        }, AUTOPLAY_MS);
        return () => clearInterval(timer.current);
    }, [count, paused]);

    if (count === 0) {
        return (
            <section
                className="relative noise-overlay overflow-hidden bg-[var(--brand-rose)]"
                style={{ minHeight: '82vh' }}
                data-testid="hero-section"
            />
        );
    }

    const active = slides[current];

    return (
        <section
            className="relative noise-overlay overflow-hidden"
            style={{ minHeight: '82vh' }}
            data-testid="hero-section"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* Slide images (crossfade) */}
            <div className="absolute inset-0">
                {slides.map((s, i) => (
                    <div
                        key={`${s.image}-${i}`}
                        aria-hidden={i !== current}
                        className="absolute inset-0 transition-opacity duration-700 ease-out"
                        style={{ opacity: i === current ? 1 : 0 }}
                    >
                        <img
                            src={s.image}
                            alt={s.headline || `Slide ${i + 1}`}
                            className="w-full h-full object-cover"
                            loading={i === 0 ? 'eager' : 'lazy'}
                        />
                    </div>
                ))}
                <div className="absolute inset-0 hero-overlay-soft" />
            </div>

            {/* Slide content */}
            <div className="relative z-10 max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32 min-h-[82vh] flex items-center justify-center">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={current}
                        initial={{ opacity: 0, y: 22 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="flex flex-col items-center text-center max-w-[900px]"
                    >
                        <h1
                            data-testid="hero-headline"
                            className="font-serif shine-text-light leading-[1.02]"
                            style={{ fontSize: 'clamp(2.75rem, 6.5vw, 6rem)' }}
                        >
                            {active.headline || 'Elegant, Effortless Style.'}
                        </h1>
                        {active.subtext && (
                            <p className="mt-5 text-white/90 max-w-xl text-base md:text-lg" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.4)' }}>
                                {active.subtext}
                            </p>
                        )}
                        {(active.cta_label) && (
                            <Link
                                to={active.cta_link || '/shop'}
                                data-testid="hero-shop-the-look-button"
                                className="mt-9 inline-flex items-center gap-3 bg-white text-[var(--brand-burgundy)] px-8 py-3.5 rounded-xl text-xs tracking-[0.22em] uppercase font-medium hover:bg-white/90 transition-all duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(0,0,0,0.22)]"
                            >
                                {active.cta_label}
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Controls (only when more than one slide) */}
            {count > 1 && (
                <>
                    <button
                        type="button"
                        onClick={prev}
                        aria-label="Previous slide"
                        data-testid="hero-slider-prev"
                        className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 z-20 grid place-items-center w-11 h-11 rounded-full bg-white/25 backdrop-blur-sm text-white hover:bg-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                        type="button"
                        onClick={next}
                        aria-label="Next slide"
                        data-testid="hero-slider-next"
                        className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-20 grid place-items-center w-11 h-11 rounded-full bg-white/25 backdrop-blur-sm text-white hover:bg-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 transition-colors"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>

                    {/* Dots */}
                    <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5" data-testid="hero-slider-dots">
                        {slides.map((s, i) => (
                            <button
                                key={`dot-${s.image}-${i}`}
                                type="button"
                                onClick={() => goTo(i)}
                                aria-label={`Go to slide ${i + 1}`}
                                aria-current={i === current}
                                data-testid={`hero-slider-dot-${i}`}
                                className="h-2 rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                                style={{
                                    width: i === current ? '26px' : '8px',
                                    backgroundColor: i === current ? '#ffffff' : 'rgba(255,255,255,0.55)',
                                }}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}
